import {
  LayoutNodeActionType,
  LayoutNodeFilter,
  LayoutNodeFilterType,
  LayoutNodeInput,
  LayoutNodeLink,
  LayoutNodeType,
} from "@towersystems/roam-common/lib/generated-types";
import { Animated } from "react-native";
import { DragDropActionTypes } from "../drag-drop";
import { Registry } from "./registry";

export type AsyncLayoutActionResult<T> = {
  success: boolean;
  result: T;
};

export type UseAnimatorReturn = {
  animationRegistry: Registry<Animated.ValueXY>;
  animationRunning: boolean;
  shift: (id: string, position: number, callback?: () => void) => void;
  homing: (id: string, position: number, callback?: () => void) => void;
  restorePositions: (callback?: () => void) => void;
};

export interface AsyncLayoutActionDefinitionConfig<
  T,
  J,
  L extends (...data: any[]) => Promise<J>,
  R extends (...data: any[]) => Promise<T>
> {
  localAction: L;
  remoteAction: R;
}

export class AsyncLayoutActionDefinition<
  T,
  J,
  L extends (...data: any[]) => Promise<J>,
  R extends (...data: any[]) => Promise<T>
> {
  constructor(public config: AsyncLayoutActionDefinitionConfig<T, J, L, R>) {}
}

export type LayoutAction = {
  type: DragDropActionTypes;
  payload: any;
};

export type UseLayoutPagesProps = {
  isEditing: boolean;
  layoutNodes: any[];
  width?: number;
  onPageChanged: (page: number) => void;
};

export type PageType = {
  page: number;
  items: PageItemType[];
};

export type PageItemType = {
  id: string;
  position: number;
  placeholder?: boolean;
  config?: ItemConfigType;
};

export type ItemConfigType = {
  label?: string;
  color?: string;
  productId?: string;
  layoutId?: string;
  showImage?: boolean;
  type?: string;
  position: number;
  action?: LayoutNodeActionType;
  link?: LayoutNodeLink;
  filter?: LayoutNodeFilter;
  imageUrl?: string;
  labelColor?: string;
};

export type Rect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type Promixity<T> = {
  distance: number;
  payload: T;
};

export type ShiftParams = {
  start: number;
  end: number;
  offset: number;
};

export interface RegistryInterface<T> {
  get(key: string): T;
  register(key: string, entry: T): void;
  has(key: string): boolean;
}

export type CreateQuickKeyPrerequisites = {
  layoutId: string;
  position: number;
};

export type CreateTileType = {
  config: CreateQuickKeyPrerequisites | null;
  openCreateTile: (config: CreateQuickKeyPrerequisites) => void;
  handleCreateTile: (input: LayoutNodeInput) => Promise<void>;
};

export type EditTileType = {
  item: PageItemType | null;
  openEditTile: (item: PageItemType) => void;
  handleUpdateTile: (input: LayoutNodeInput) => Promise<void>;
  handleRemoveTile: (id: string) => Promise<void>;
};

export type LayoutDimensions = {
  rows: number;
  columns: number;
  itemsPerPage: number;
  width: number;
};

export type TypeListItem =
  | LayoutNodeType
  | LayoutNodeActionType
  | LayoutNodeFilterType;

export type QuickKeyGroup = {
  label: string;
  typeListItems: TypeListItem[];
};
